/**
 * Orders in these statuses are "closed" for the purpose of allowing a new customer order.
 * Blocking statuses: pending, approved only.
 */
export const CREATE_ORDER_BLOCKED_MESSAGE =
  "You can create a new order only after your current order is delivered or cancelled.";

export const ADD_LOCKER_ITEMS_BLOCKED_MESSAGE =
  "Please create an order before adding items.";

export const ORDER_PAID_ADD_ITEMS_BLOCKED_MESSAGE =
  "Your current order is  delivered. Please create a new order to add items.";

export const LOCKER_INACTIVE_ITEMS_MESSAGE =
  "Your virtual locker is currently inactive. Create a new order to activate your locker.";

/** Inactive locker when customer already has an order — no "create order" CTA. */
export const LOCKER_INACTIVE_ONLY_MESSAGE =
  "Your virtual locker is currently inactive.";

export const LOCKER_EXPIRED_ITEMS_MESSAGE =
  "Your locker has expired. You cannot add new items.";

export const VIRTUAL_LOCKER_REQUIRED_MESSAGE =
  "Please create a Virtual Locker before creating an order.";

/** inactive | active | expired */
export function resolveLockerStatus(locker, activeOrderStatus = null) {
  if (!locker || Array.isArray(locker)) return "inactive";

  // If customer has an in-flight order, locker must be treated as active
  // regardless of stored locker_status (guards against stale inactive state)
  const IN_FLIGHT_STATUSES = ['pending', 'approved', 'bill_generated', 'paid', 'packing', 'shipment'];
  if (activeOrderStatus && IN_FLIGHT_STATUSES.includes(String(activeOrderStatus).toLowerCase())) {
    const expiry = locker.locker_expiry_date;
    if (expiry) {
      const expiryDate = new Date(expiry);
      if (!Number.isNaN(expiryDate.getTime()) && expiryDate >= new Date()) return "active";
    }
  }

  const stored = locker.locker_status;
  if (stored === "inactive") return "inactive";
  if (stored === "expired") return "expired";

  if (stored === "active") {
    const expiry = locker.locker_expiry_date;
    if (!expiry) return "inactive";
    const expiryDate = new Date(expiry);
    if (Number.isNaN(expiryDate.getTime())) return "inactive";
    return expiryDate >= new Date() ? "active" : "expired";
  }

  const expiry = locker.locker_expiry_date;
  if (!expiry) return "inactive";
  const expiryDate = new Date(expiry);
  if (Number.isNaN(expiryDate.getTime())) return "inactive";
  return expiryDate >= new Date() ? "active" : "expired";
}

export function isLockerActiveForItems(locker) {
  return resolveLockerStatus(locker) === "active";
}

/** True when customer has at least one non-closed order in flight. */
export function hasActiveCustomerOrder(
  orderSummary,
  hasFetchedOrderList = true,
  recordList
) {
  if (!hasFetchedOrderList) return true;

  const n = orderSummary?.blocking_create_order_count;
  if (typeof n === "number") return n > 0;

  // Fallback from list when summary is missing
  const CLOSED_STATUSES = ["delivered", "rejected", "cancelled"];
  const orders = recordList ?? [];
  if (orders.length > 0) {
    return orders.some(
      (o) =>
        !CLOSED_STATUSES.includes(String(o?.order_status || "").toLowerCase())
    );
  }

  return false;
}

/** First blocking reason for adding locker items, or null when allowed. */
export function getLockerItemBlockReason(
  locker,
  orderSummary,
  hasFetchedOrderList = true,
  recordList
) {
  if (!hasFetchedOrderList) {
    return ADD_LOCKER_ITEMS_BLOCKED_MESSAGE;
  }

  const lockerStatus = resolveLockerStatus(locker);
  if (lockerStatus === "expired") {
    return LOCKER_EXPIRED_ITEMS_MESSAGE;
  }
  // Always block new items when locker itself is inactive
  if (lockerStatus === "inactive") {
    const hasOrder = hasActiveCustomerOrder(
      orderSummary,
      hasFetchedOrderList,
      recordList
    );
    // Order already exists → only inactive message (no create-order CTA)
    return hasOrder
      ? LOCKER_INACTIVE_ONLY_MESSAGE
      : LOCKER_INACTIVE_ITEMS_MESSAGE;
  }

  // If no pending/approved order exists but a paid/packing/shipment order does,
  // block adding items — user must create a new order first
  const PENDING_STATUSES = ['pending', 'approved', 'bill_generated'];
  const PAID_STATUSES = ['paid', 'packing', 'shipment'];
  const orders = recordList ?? [];
  const hasPendingOrder = orders.some((o) =>
    PENDING_STATUSES.includes(String(o?.order_status || '').toLowerCase())
  );
  const hasPaidOrder = orders.some((o) =>
    PAID_STATUSES.includes(String(o?.order_status || '').toLowerCase())
  );
  if (!hasPendingOrder && hasPaidOrder) {
    return ORDER_PAID_ADD_ITEMS_BLOCKED_MESSAGE;
  }

  const hasOrder = hasActiveCustomerOrder(
    orderSummary,
    hasFetchedOrderList,
    recordList
  );
  if (!hasOrder) {
    return ADD_LOCKER_ITEMS_BLOCKED_MESSAGE;
  }

  return null;
}

/** True when customer may add locker items (active locker + active order). */
export function canAddVirtualLockerItems(
  orderSummary,
  hasFetchedOrderList = true,
  recordList,
  locker
) {
  return !getLockerItemBlockReason(
    locker,
    orderSummary,
    hasFetchedOrderList,
    recordList
  );
}

export function canCreateNewCustomerOrder(
  orderSummary,
  hasFetchedOrderList = true,
  recordList,
  virtualAddress
) {
  if (!hasFetchedOrderList) return false;

  const hasLocker =
    virtualAddress &&
    !Array.isArray(virtualAddress) &&
    Boolean(virtualAddress.locker_id);
  if (!hasLocker) return false;

  return !hasActiveCustomerOrder(orderSummary, hasFetchedOrderList, recordList);
}
